import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { IProduct, IProductVariation, IVariationAsset } from 'src/interfaces';
import { getThumbnailSrc } from 'src/utils/gcf-helper';

import { sortProductVariation } from '../modules/EditProductPage.helper';
import {
  dataURLtoFile,
  normalizeValue,
  stripDisplayFields,
  TEMP_STORED_PRODUCT_DATA,
  TEMP_STORED_PRODUCT_FILE,
} from './EditProductPage.helper';

export const useEditProductPage = (
  productData?: IProduct,
  allVariationAssets?: IVariationAsset[],
  viewAssetsWithComponents?: IVariationAsset[],
) => {
  const tempStoredProductVariations = useRef(
    sessionStorage.getItem(TEMP_STORED_PRODUCT_DATA(productData?.product_code)),
  );
  const parsedtTempStoredProductVariations = tempStoredProductVariations.current
    ? JSON.parse(tempStoredProductVariations.current)
    : null;

  const tempStoredProductFile = useRef(
    sessionStorage.getItem(TEMP_STORED_PRODUCT_FILE(productData?.product_code)),
  );
  const parsedtTempStoredProductFile = tempStoredProductFile.current
    ? JSON.parse(tempStoredProductFile.current)
    : null;

  const baselineRef = useRef<any>(null);
  const skipBlockRef = useRef(false);

  // Baseline — always clean server data, never sessionStorage
  const computeBaseline = useCallback(
    (
      productData?: IProduct,
      allVariationAssets?: IVariationAsset[],
      viewAssetsWithComponents?: IVariationAsset[],
    ) => {
      const mapAllVariationAssets = allVariationAssets?.map((asset, index) => ({
        ...asset,
        in_use: viewAssetsWithComponents?.[index]?.in_use,
      }));

      return {
        active: productData?.active as boolean,
        asset_id: productData?.asset_id,
        product_code: productData?.product_code || '',
        name_override: productData?.name_override || '',
        product_category_id: productData?.product_category_id
          ? productData.product_category_id
          : [],
        product_category_names: productData?.product_category_names || [],
        productVariations:
          (sortProductVariation(
            productData?.productVariations?.map((productVariation) => {
              const variationAssets = mapAllVariationAssets
                ?.filter((asset: IVariationAsset) =>
                  productVariation?.asset_id?.includes(asset.id),
                )
                .map((asset: IVariationAsset) => ({
                  assetName: asset.name,
                  assetUrl: getThumbnailSrc(asset.name),
                  themeIds: asset.theme_id,
                  inUse: asset.in_use,
                  assetId: asset.id,
                  isDY: asset.id === productVariation?.dy_asset,
                }));

              return {
                product_variation_size_id:
                  productVariation.product_variation_size_id || 0,
                product_variation_flavor_id:
                  productVariation.product_variation_flavor_id,
                product_code: productVariation.product_code || '',
                name: productVariation.name || '',
                active: productVariation.active ?? true,
                description: productVariation.description || '',
                disclaimer: productVariation.disclaimer || '',
                nutrition: productVariation.nutrition || '',
                volume: productVariation.volume || 0,
                created_at: productVariation.created_at || '',
                modified_at: productVariation.modified_at || '',
                group_product_code: productData!.product_code,
                legal: productVariation.legal || '',
                asset_id: productVariation.asset_id || [],
                dy_asset: productVariation.dy_asset || null,
                variationAssets,
                variation_template_id: productVariation.variation_template_id,
                variation_template: productVariation.variation_template,
              };
            }),
          ) as IProductVariation[]) || [],
      } as unknown as IProduct;
    },
    [],
  );

  // Form initial values — uses sessionStorage if available (restores user edits)
  const initializeFormValue = useCallback(
    (
      productData?: IProduct,
      allVariationAssets?: IVariationAsset[],
      viewAssetsWithComponents?: IVariationAsset[],
    ) => {
      const baseline = computeBaseline(
        productData,
        allVariationAssets,
        viewAssetsWithComponents,
      );

      if (!parsedtTempStoredProductVariations) {
        return baseline;
      }
      let asset: File | undefined;
      if (parsedtTempStoredProductFile?.dataURL) {
        asset = dataURLtoFile(
          parsedtTempStoredProductFile.dataURL,
          parsedtTempStoredProductFile.name,
        );
      }

      return {
        ...parsedtTempStoredProductVariations,
        asset,
        productVariations:
          parsedtTempStoredProductVariations.productVariations.map(
            (variation: IProductVariation) => {
              const baselineVariation = baseline.productVariations.find(
                (bv) => bv.product_code === variation.product_code,
              );

              return {
                ...variation,
                variation_template: baselineVariation?.variation_template,
                variation_template_id: baselineVariation?.variation_template_id,
                variationAssets: baselineVariation?.variationAssets,
                asset_id: baselineVariation?.asset_id,
                dy_asset: baselineVariation?.dy_asset,
              };
            },
          ),
      };
    },
    [
      parsedtTempStoredProductVariations,
      parsedtTempStoredProductFile,
      computeBaseline,
    ],
  );

  const formMethod = useForm<IProduct & { asset?: File }>({
    // `values` (not `defaultValues`) so the form re-syncs when productData
    // refetches — e.g. after a variation is deleted. `defaultValues` only
    // applies on mount, which left the deleted variation in the form and let
    // Save re-create it via the PUT/upsert (CC-23475). sessionStorage restore
    // is preserved because initializeFormValue reads it as its source.
    values: initializeFormValue(
      productData,
      allVariationAssets,
      viewAssetsWithComponents,
    ),
  });

  const shouldBlock = useCallback(() => {
    if (skipBlockRef.current) return false;

    const { asset: currentAsset, ...currentRest } = formMethod.getValues();
    const baseline = baselineRef.current;

    if (!baseline) return false;

    const { asset: baselineAsset, ...baselineRest } = baseline;

    return (
      !isEqual(
        normalizeValue(stripDisplayFields(currentRest)),
        normalizeValue(stripDisplayFields(baselineRest)),
      ) || currentAsset !== baselineAsset
    );
  }, [formMethod]);

  const skipNextBlock = useCallback(() => {
    skipBlockRef.current = true;
  }, []);

  useEffect(() => {
    // Wait until all data is ready — undefined means not yet loaded
    if (
      !productData ||
      allVariationAssets === undefined ||
      viewAssetsWithComponents === undefined
    )
      return;

    // Keep the dirty-check baseline in sync with the latest server data.
    // The form itself is re-synced by the `values` option above — no manual
    // reset here, so a one-shot guard is no longer needed (a guarded reset was
    // what stopped the form reflecting a deleted variation — CC-23475).
    skipBlockRef.current = false;
    baselineRef.current = computeBaseline(
      productData,
      allVariationAssets,
      viewAssetsWithComponents,
    );
  }, [
    productData,
    allVariationAssets,
    viewAssetsWithComponents,
    computeBaseline,
  ]);

  const resetFormValue = () => {
    tempStoredProductVariations.current = null;
    tempStoredProductFile.current = null;
    const values = computeBaseline(
      productData,
      allVariationAssets,
      viewAssetsWithComponents,
    );
    baselineRef.current = values;
    formMethod.reset(values);
  };

  useEffect(() => {
    sessionStorage.removeItem(
      TEMP_STORED_PRODUCT_DATA(productData?.product_code),
    );
  }, [productData?.product_code]);

  return {
    formMethod,
    resetFormValue,
    shouldBlock,
    skipNextBlock,
  };
};
